import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { environment } from '../../config/environment'

interface Guild {
  id: string
  name: string
  level: number
  memberCount: number
  ownerId: string
}

export function GuildPanel(): React.ReactElement {
  const [guilds, setGuilds] = useState<Guild[]>([])
  const [loading, setLoading] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newGuildName, setNewGuildName] = useState('')

  useEffect(() => {
    fetchGuilds()
  }, [])

  const fetchGuilds = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('authToken')
      const response = await axios.get(`${environment.API_URL}/guilds`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setGuilds(response.data)
    } catch (error) {
      console.error('Failed to fetch guilds', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateGuild = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newGuildName.trim()) return

    try {
      const token = localStorage.getItem('authToken')
      await axios.post(
        `${environment.API_URL}/guilds`,
        { name: newGuildName },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setNewGuildName('')
      setShowCreateForm(false)
      fetchGuilds()
    } catch (error) {
      console.error('Failed to create guild', error)
    }
  }

  const handleJoinGuild = async (guildId: string) => {
    try {
      const token = localStorage.getItem('authToken')
      await axios.post(
        `${environment.API_URL}/guilds/join`,
        { guildId },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      fetchGuilds()
    } catch (error) {
      console.error('Failed to join guild', error)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 max-w-md">
      <h2 className="text-xl font-bold mb-4 text-gray-800">⚔️ Guilds</h2>

      {showCreateForm && (
        <form onSubmit={handleCreateGuild} className="mb-4 p-3 bg-purple-50 rounded">
          <input
            type="text"
            placeholder="Guild name"
            value={newGuildName}
            onChange={(e) => setNewGuildName(e.target.value)}
            className="w-full px-2 py-1 border border-purple-300 rounded mb-2 text-sm"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              className="flex-1 bg-purple-500 text-white py-1 rounded font-semibold hover:bg-purple-600 text-sm"
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              className="flex-1 bg-gray-400 text-white py-1 rounded font-semibold hover:bg-gray-500 text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {!showCreateForm && (
        <button
          onClick={() => setShowCreateForm(true)}
          className="w-full mb-4 bg-purple-500 text-white py-2 rounded font-semibold hover:bg-purple-600"
        >
          + Create Guild
        </button>
      )}

      {loading ? (
        <p className="text-center text-gray-500">Loading...</p>
      ) : guilds.length === 0 ? (
        <p className="text-center text-gray-500">No guilds found</p>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {guilds.map((guild) => (
            <div key={guild.id} className="border-2 border-purple-200 rounded-lg p-3">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-semibold text-gray-800">{guild.name}</p>
                  <p className="text-sm text-gray-600">Level {guild.level}</p>
                </div>
                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                  {guild.memberCount} members
                </span>
              </div>
              <button
                onClick={() => handleJoinGuild(guild.id)}
                className="w-full bg-blue-500 text-white py-1 rounded font-semibold hover:bg-blue-600 text-sm"
              >
                Join Guild
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
